import React from 'react';
import {
    LayoutDashboard,
    Users,
    GraduationCap,
    UserCheck,
    Settings,
    LogOut,
    X,
    BookOpen,
    Star,
    Calendar,
    FileText,
    DollarSign,
    Receipt,
    Award,
    CheckCircle,
    Clock,
    Coins,
    School,
    Wallet,
    Wifi,
    Cpu,
    ShieldCheck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

interface SuperAdminSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    currentPath: string;
}

const SuperAdminSidebar: React.FC<SuperAdminSidebarProps> = ({ isOpen, onClose, currentPath }) => {
    const { user, signOut } = useAuth();
    const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

    const toggleSubmenu = (name: string) => {
        setExpandedItems(prev =>
            prev.includes(name)
                ? prev.filter(item => item !== name)
                : [...prev, name]
        );
    };

    interface NavigationItem {
        name: string;
        href?: string;
        icon: any;
        children?: NavigationItem[];
    }

    const navigation: NavigationItem[] = [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Tenant Access', href: '/tenant-access', icon: ShieldCheck },
        { name: 'Device Configuration', href: '/device-configuration', icon: Wifi },
    ];

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const renderNavItem = (item: NavigationItem) => {
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = expandedItems.includes(item.name);
        const isActive = item.href ? currentPath === item.href : false;

        return (
            <div key={item.name} className="space-y-1">
                {item.href ? (
                    <Link
                        to={item.href || ''}
                        onClick={onClose}
                        className={`flex items-center justify-between px-4 py-3 text-sm font-bold rounded-2xl transition-all duration-300 group ${
                            isActive
                                ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/10 text-emerald-400 border border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                                : 'text-slate-400 hover:text-emerald-400 hover:bg-slate-900/60 border border-transparent'
                        }`}
                    >
                        <div className="flex items-center space-x-3">
                            <item.icon className={`h-5 w-5 transition-transform duration-300 group-hover:scale-110 ${isActive ? 'text-emerald-400' : 'text-slate-500 group-hover:text-emerald-400'}`} />
                            <span className="tracking-wide">{item.name}</span>
                        </div>
                    </Link>
                ) : (
                    <button
                        onClick={() => toggleSubmenu(item.name)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-bold rounded-2xl transition-all duration-300 group ${
                            isExpanded
                                ? 'text-slate-200 bg-slate-900/40 border border-slate-800'
                                : 'text-slate-400 hover:text-emerald-400 hover:bg-slate-900/60 border border-transparent'
                        }`}
                    >
                        <div className="flex items-center space-x-3">
                            <item.icon className="h-5 w-5 text-slate-500 group-hover:text-emerald-400" />
                            <span className="tracking-wide">{item.name}</span>
                        </div>
                        <svg
                            className={`h-4 w-4 text-slate-500 group-hover:text-emerald-400 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                )}

                {hasChildren && isExpanded && (
                    <div className="pl-4 pr-1 py-1 space-y-1 bg-slate-950/40 rounded-2xl border border-slate-900/60 mt-1">
                        {item.children!.map(child => {
                            const isChildActive = child.href ? currentPath === child.href : false;
                            return (
                                <Link
                                    key={child.name}
                                    to={child.href || ''}
                                    onClick={onClose}
                                    className={`flex items-center space-x-3 px-4 py-2.5 text-xs font-bold rounded-xl transition-all duration-300 group ${
                                        isChildActive
                                            ? 'text-emerald-400 bg-emerald-500/10'
                                            : 'text-slate-500 hover:text-emerald-400 hover:bg-slate-900/30'
                                    }`}
                                >
                                    <child.icon className={`h-4.5 w-4.5 ${isChildActive ? 'text-emerald-400' : 'text-slate-600 group-hover:text-emerald-400'}`} />
                                    <span className="tracking-wider">{child.name}</span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {/* Sidebar container */}
            <div
                className={`fixed inset-y-0 left-0 z-50 w-72 bg-slate-950 border-r border-emerald-500/10 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto flex flex-col h-screen ${
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-emerald-500/20">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                            <Cpu className="h-6 w-6 text-slate-950 stroke-[2px]" />
                        </div>
                        <div>
                            <h1 className="text-sm font-black tracking-widest text-slate-100 uppercase">EDULINK</h1>
                            <span className="text-[9px] font-black tracking-widest text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1 py-0.2 rounded uppercase">
                                Root Portal
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg border border-slate-800 text-slate-400 hover:text-emerald-400 hover:bg-slate-900 lg:hidden transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Navigation Menu */}
                <div className="flex-1 px-4 py-6 overflow-y-auto space-y-6 custom-scrollbar">
                    <div className="space-y-1.5">
                        <span className="px-4 text-[9px] font-black uppercase text-slate-600 tracking-widest block mb-2">SYSTEM SHELL</span>
                        {navigation.map(renderNavItem)}
                    </div>
                </div>

                {/* User Profile / Logout footer */}
                <div className="p-4 border-t border-emerald-500/20 bg-slate-900/20">
                    <div className="flex items-center justify-between p-3 bg-slate-900/60 border border-slate-850 rounded-2xl">
                        <div className="flex items-center space-x-3 overflow-hidden">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center font-bold text-slate-950 uppercase shrink-0">
                                SA
                            </div>
                            <div className="overflow-hidden">
                                <h4 className="text-xs font-bold text-slate-200 truncate">Super Admin</h4>
                                <p className="text-[10px] text-slate-500 truncate font-mono">{user?.email}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleSignOut}
                            className="p-2 rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-300"
                            title="Terminate Gateway Session"
                        >
                            <LogOut className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Backdrop for mobile */}
            {isOpen && (
                <div
                    onClick={onClose}
                    className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm lg:hidden transition-opacity duration-300"
                />
            )}
        </>
    );
};

export default SuperAdminSidebar;
